import {
  fetchDayOfEmergencyAnonymousAccessListMapLocationsForRecipient,
  fetchDayOfEmergencyAnonymousAccessPatchParticipantSelfForRecipient,
  fetchDayOfEmergencyAnonymousAccessValidateRecipient,
} from "@crowdcoursing/api/openapi";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { useToast } from "~hooks/useToast";
import { LAW_ENFORCEMENT_ACCESS_TOKEN_HEADER } from "~services/lawEnforcementEmergencyCommandCenterApi";
import { useBackendUrl } from "~services/providers/BackendUrlProvider";

import { STATUS_OPTIONS, statusLabelForKey } from "../VisitorManagement/emergencyCommandCenterShared";
import { MY_LOCATION_MAP_VALUE } from "../VisitorManagement/EmergencyParticipantStatusDialog";

/** Statuses shown on the minimal quick-check page (excludes “not reported”). */
const QUICK_STATUS_OPTIONS = STATUS_OPTIONS.filter((o) => o.value !== "not-reported");

type GeoState = "idle" | "prompting" | "granted" | "denied" | "unavailable";

export function QuickStatusCheckPage() {
  const { dayOfEmergencyId = "", token = "" } = useParams<{ dayOfEmergencyId: string; token: string }>();
  const { backendUrl } = useBackendUrl();
  const toast = useToast();

  const [mapLocationId, setMapLocationId] = useState<string>("");
  const [geoState, setGeoState] = useState<GeoState>("idle");
  const [userCoords, setUserCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [submittingKey, setSubmittingKey] = useState<string | null>(null);
  const defaultLocationAppliedRef = useRef(false);

  const validationQuery = useQuery({
    enabled: Boolean(backendUrl && dayOfEmergencyId && token),
    queryFn: ({ signal }) =>
      fetchDayOfEmergencyAnonymousAccessValidateRecipient(
        {
          baseUrlOverride: backendUrl,
          body: { token },
          pathParams: { dayOfEmergencyId },
        },
        signal,
      ),
    queryKey: ["quick-status-validate", dayOfEmergencyId, token],
    retry: false,
  });

  const isActiveEmergencyForRecipient = validationQuery.data?.isActiveEmergencyForRecipient === true;

  const mapLocationsQuery = useQuery({
    enabled:
      Boolean(backendUrl && dayOfEmergencyId && token) && validationQuery.isSuccess && isActiveEmergencyForRecipient,
    queryFn: ({ signal }) =>
      fetchDayOfEmergencyAnonymousAccessListMapLocationsForRecipient(
        {
          baseUrlOverride: backendUrl,
          headers: { [LAW_ENFORCEMENT_ACCESS_TOKEN_HEADER]: token },
          pathParams: { dayOfEmergencyId },
        },
        signal,
      ),
    queryKey: ["quick-status-map-locations", dayOfEmergencyId, token],
    retry: false,
  });

  useEffect(() => {
    if (!backendUrl || !dayOfEmergencyId || !token) {
      return;
    }
    if (!navigator.geolocation) {
      setGeoState("unavailable");
      return;
    }
    setGeoState("prompting");
    setUserCoords(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGeoState("granted");
      },
      () => {
        setGeoState("denied");
        setUserCoords(null);
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 25_000 },
    );
  }, [backendUrl, dayOfEmergencyId, token]);

  useEffect(() => {
    if (geoState !== "denied" && geoState !== "unavailable") {
      return;
    }
    setMapLocationId((prev) => (prev === MY_LOCATION_MAP_VALUE ? "" : prev));
  }, [geoState]);

  useEffect(() => {
    if (geoState !== "granted" || !userCoords || defaultLocationAppliedRef.current) {
      return;
    }
    if (mapLocationId !== "") {
      defaultLocationAppliedRef.current = true;
      return;
    }
    setMapLocationId(MY_LOCATION_MAP_VALUE);
    defaultLocationAppliedRef.current = true;
  }, [geoState, mapLocationId, userCoords]);

  const showMyLocationOption = (geoState === "granted" && userCoords !== null) || false;
  const mapLocationSelectValue = mapLocationId === MY_LOCATION_MAP_VALUE && !showMyLocationOption ? "" : mapLocationId;

  const resolveLocationForPatch = useCallback(async (): Promise<{
    bubbleMapLocationId: string | null;
    reportedLatitude: number | null;
    reportedLongitude: number | null;
  }> => {
    const raw = mapLocationId.trim();
    if (raw === MY_LOCATION_MAP_VALUE) {
      const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error("Location is not available."));
          return;
        }
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          maximumAge: 0,
          timeout: 20_000,
        });
      });
      return {
        bubbleMapLocationId: null,
        reportedLatitude: pos.coords.latitude,
        reportedLongitude: pos.coords.longitude,
      };
    }
    if (raw) {
      return { bubbleMapLocationId: raw, reportedLatitude: null, reportedLongitude: null };
    }
    return { bubbleMapLocationId: null, reportedLatitude: null, reportedLongitude: null };
  }, [mapLocationId]);

  const onReportStatus = useCallback(
    async (statusKey: string) => {
      if (!backendUrl || !dayOfEmergencyId || !token) {
        return;
      }
      setSubmittingKey(statusKey);
      try {
        const loc = await resolveLocationForPatch();
        await fetchDayOfEmergencyAnonymousAccessPatchParticipantSelfForRecipient({
          baseUrlOverride: backendUrl,
          body: {
            bubbleMapLocationId: loc.bubbleMapLocationId,
            reportedLatitude: loc.reportedLatitude,
            reportedLongitude: loc.reportedLongitude,
            statusKey,
          },
          headers: { [LAW_ENFORCEMENT_ACCESS_TOKEN_HEADER]: token },
          pathParams: { dayOfEmergencyId },
        });
        toast.success(`Updated: ${statusLabelForKey(statusKey)}`);
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Could not update status.");
      } finally {
        setSubmittingKey(null);
      }
    },
    [backendUrl, dayOfEmergencyId, resolveLocationForPatch, toast, token],
  );

  if (!backendUrl) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!dayOfEmergencyId || !token) {
    return (
      <Box sx={{ boxSizing: "border-box", maxWidth: 480, mx: "auto", p: 2 }}>
        <Alert severity="warning">This link is incomplete. Use the URL from your text message.</Alert>
      </Box>
    );
  }

  if (validationQuery.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (validationQuery.isError) {
    return (
      <Box sx={{ boxSizing: "border-box", maxWidth: 480, mx: "auto", p: 2 }}>
        <Alert severity="error">This link is not valid or has expired.</Alert>
      </Box>
    );
  }

  if (validationQuery.isSuccess && !isActiveEmergencyForRecipient) {
    return (
      <Box sx={{ boxSizing: "border-box", maxWidth: 480, mx: "auto", p: 2 }}>
        <Alert severity="info">
          <Typography component="div" sx={{ fontWeight: 600, mb: 0.5 }} variant="subtitle1">
            The emergency has ended
          </Typography>
          <Typography variant="body2">
            Thanks for checking in. There is no active emergency right now, so no further action is needed.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        maxWidth: 480,
        minWidth: 0,
        mx: "auto",
        p: { md: 3, xs: 2 },
        width: "100%",
      }}
    >
      <Typography component="h1" sx={{ mb: 2 }} variant="h5">
        Report your status
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
        Choose your location, then tap a status. You can update as often as your situation changes.
      </Typography>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="quick-status-loc-label">Location</InputLabel>
        <Select
          disabled={mapLocationsQuery.isLoading}
          label="Location"
          labelId="quick-status-loc-label"
          onChange={(e) => setMapLocationId(String(e.target.value))}
          value={mapLocationSelectValue}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {showMyLocationOption ? <MenuItem value={MY_LOCATION_MAP_VALUE}>My location (GPS)</MenuItem> : null}
          {(mapLocationsQuery.data ?? [])
            .filter((l) => l.isActive !== false)
            .map((l) => (
              <MenuItem key={String(l.id)} value={String(l.id)}>
                {l.label}
              </MenuItem>
            ))}
        </Select>
        {geoState === "prompting" ? (
          <FormHelperText sx={{ alignItems: "center", display: "flex", gap: 1, mt: 1 }}>
            <CircularProgress size={14} />
            Getting location…
          </FormHelperText>
        ) : null}
        {geoState === "denied" || geoState === "unavailable" ? (
          <FormHelperText>
            {geoState === "unavailable"
              ? "Location is not supported. Pick a school location or None."
              : "Location permission denied. Pick a school location or None."}
          </FormHelperText>
        ) : null}
        {mapLocationId === MY_LOCATION_MAP_VALUE && showMyLocationOption ? (
          <FormHelperText>Each update uses a fresh GPS reading when you tap a status.</FormHelperText>
        ) : null}
      </FormControl>

      <Stack spacing={1.25}>
        {QUICK_STATUS_OPTIONS.map((o) => (
          <Button
            disabled={Boolean(submittingKey) || mapLocationsQuery.isLoading}
            fullWidth
            key={o.value}
            onClick={() => void onReportStatus(o.value)}
            size="large"
            variant="contained"
          >
            {submittingKey === o.value ? <CircularProgress color="inherit" size={22} /> : o.label}
          </Button>
        ))}
      </Stack>
    </Box>
  );
}
